import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage    from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ConfirmPage  from './pages/ConfirmPage';
import ForgotPage   from './pages/ForgotPage';
import CallbackPage from './pages/CallbackPage';
import AdminPage       from './pages/AdminPage';
import AppLayout from './components/AppLayout';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import AuthorizationsPage from './pages/AuthorizationsPage';
import ReferralsPage from './pages/ReferralsPage';
import ScheduleRunsPage from './pages/ScheduleRunsPage';
import MailboxCleanupPage from './pages/MailboxCleanupPage';
import PaymentsPage from './pages/PaymentsPage';
import PaymentsImportPage from './pages/PaymentsImportPage';
import PayersPage from './pages/PayersPage';
import SettingsPage from './pages/SettingsPage';
import LegacyAdminRedirect from './pages/LegacyAdminRedirect';
import AdminsOnlyPage from './pages/AdminsOnlyPage';
import MarketingLayout from './marketing/MarketingLayout';
import MarketingDashboardPage from './marketing/pages/DashboardPage';
import MarketingPostsPage from './marketing/pages/PostsPage';
import MarketingEscalationsPage from './marketing/pages/EscalationsPage';
import MarketingLeadsPage from './marketing/pages/LeadsPage';
import MarketingCommentsPage from './marketing/pages/CommentsPage';
import MarketingMessagesPage from './marketing/pages/MessagesPage';
import MarketingBrandVoicePage from './marketing/pages/BrandVoicePage';
import MarketingBrandImagesPage from './marketing/pages/BrandImagesPage';
import MarketingAuditLogPage from './marketing/pages/AuditLogPage';
import MarketingSettingsPage from './marketing/pages/SettingsPage';
import OutreachLayout from './outreach/OutreachLayout';
import OutreachOverviewPage from './outreach/pages/OverviewPage';
import OutreachOfficesPage from './outreach/pages/OfficesPage';
import OutreachAreasPage from './outreach/pages/AreasPage';
import OutreachTrackerPage from './outreach/pages/TrackerPage';
import OutreachRunsPage from './outreach/pages/RunsPage';
import OutreachSettingsPage from './outreach/pages/SettingsPage';
import OutreachImportPage from './outreach/pages/ImportPage';
import './App.css';
import './styles/operations.css';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'sans-serif', maxWidth: 600, margin: '60px auto' }}>
          <h2 style={{ color: '#c0392b' }}>Something went wrong</h2>
          <p style={{ color: '#555', marginBottom: 16 }}>
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <button onClick={() => window.location.replace('/login')}
            style={{ padding: '10px 20px', background: '#1a2b4a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Return to Login
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// The operations platform is for administrators. Caregivers see a short page that
// points them to the Progress Notes app, without the admin shell.
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <AdminsOnlyPage />;
  return children;
}

// Belt and braces for routes rendered inside the shell.
function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user?.isAdmin) return <AdminsOnlyPage />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* /callback is always accessible. It handles the Cognito hosted UI redirect. */}
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/confirm"  element={<PublicRoute><ConfirmPage /></PublicRoute>} />
          <Route path="/forgot"   element={<PublicRoute><ForgotPage /></PublicRoute>} />

          {/* Everything signed-in shares one shell: sidebar plus page outlet. */}
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<AdminDashboardPage />} />

            {/* Care. Progress notes and the AI Agent pages stay in the separate notes app. */}
            <Route path="/clients" element={<AdminOnly><ClientsPage /></AdminOnly>} />
            <Route path="/clients/manage" element={<AdminOnly><AdminPage section="clients" /></AdminOnly>} />
            <Route path="/clients/:clientId" element={<AdminOnly><ClientDetailPage /></AdminOnly>} />
            <Route path="/clients/:clientId/:tab" element={<AdminOnly><ClientDetailPage /></AdminOnly>} />
            <Route path="/authorizations" element={<AdminOnly><AuthorizationsPage /></AdminOnly>} />
            <Route path="/referrals" element={<AdminOnly><ReferralsPage /></AdminOnly>} />

            {/* Finance */}
            <Route path="/payments" element={<AdminOnly><PaymentsPage /></AdminOnly>} />
            <Route path="/payments/import" element={<AdminOnly><PaymentsImportPage /></AdminOnly>} />
            <Route path="/payments/payers" element={<AdminOnly><PayersPage /></AdminOnly>} />

            {/* Marketing (ported from admin.homecareofmi.com) */}
            <Route path="/marketing" element={<AdminOnly><MarketingLayout /></AdminOnly>}>
              <Route index element={<MarketingDashboardPage />} />
              <Route path="posts" element={<MarketingPostsPage />} />
              <Route path="escalations" element={<MarketingEscalationsPage />} />
              <Route path="leads" element={<MarketingLeadsPage />} />
              <Route path="comments" element={<MarketingCommentsPage />} />
              <Route path="messages" element={<MarketingMessagesPage />} />
              <Route path="brand-voice" element={<MarketingBrandVoicePage />} />
              <Route path="brand-images" element={<MarketingBrandImagesPage />} />
              <Route path="audit" element={<MarketingAuditLogPage />} />
              <Route path="settings" element={<MarketingSettingsPage />} />
            </Route>

            {/* Provider outreach (Medicaid outreach intelligence agent) */}
            <Route path="/outreach" element={<AdminOnly><OutreachLayout /></AdminOnly>}>
              <Route index element={<OutreachOverviewPage />} />
              <Route path="offices" element={<OutreachOfficesPage />} />
              <Route path="areas" element={<OutreachAreasPage />} />
              <Route path="tracker" element={<OutreachTrackerPage />} />
              <Route path="runs" element={<OutreachRunsPage />} />
              <Route path="settings" element={<OutreachSettingsPage />} />
              <Route path="import" element={<OutreachImportPage />} />
            </Route>

            {/* System. Users includes accounts, invite codes and the per-caregiver SMS reminder settings. */}
            <Route path="/users" element={<AdminOnly><AdminPage section="users" /></AdminOnly>} />
            <Route path="/schedule-runs" element={<AdminOnly><ScheduleRunsPage /></AdminOnly>} />
            <Route path="/mail-cleanup" element={<AdminOnly><MailboxCleanupPage /></AdminOnly>} />
            <Route path="/settings" element={<AdminOnly><SettingsPage /></AdminOnly>} />

            {/* Old bookmarks and the prior-auth alert email */}
            <Route path="/admin" element={<AdminOnly><LegacyAdminRedirect /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}
